import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getGuardrailsResources } from '../../../services/cloudopsApi';
import { FiServer as Server } from 'react-icons/fi';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';

interface GuardrailResource {
  id: string;
  resource_type: string;
  resource_id: string;
  arn?: string;
  region: string;
  last_seen_at: string;
  is_deleted: boolean;
}

interface ResourcePaginationMeta {
  total?: number;
  totalPages?: number;
  page?: number;
  limit?: number;
}

const ResourcesPage: React.FC = () => {
  const { environmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [resources, setResources] = useState<GuardrailResource[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<ResourcePaginationMeta | null>(null);
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>(searchParams.get('resource_type') || 'all');
  const [currentPage, setCurrentPage] = useState<number>(Number.parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState<number>(Number.parseInt(searchParams.get('limit') || '20', 10));

  useEffect(() => {
    setCurrentPage(1);
  }, [resourceTypeFilter]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (resourceTypeFilter !== 'all') params.set('resource_type', resourceTypeFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 20) params.set('limit', String(pageSize));
    setSearchParams(params, { replace: true });
  }, [resourceTypeFilter, currentPage, pageSize, setSearchParams]);

  const loadResources = useCallback(async () => {
    if (!environmentId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await getGuardrailsResources(
        environmentId,
        resourceTypeFilter === 'all' ? undefined : resourceTypeFilter,
        currentPage,
        pageSize
      );
      const data = response?.data ?? (Array.isArray(response) ? response : []);
      const meta = response?.meta ?? null;
      setResources(data);
      setPaginationMeta(meta);
    } catch (err: unknown) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
      setResources([]);
      setPaginationMeta(null);
    } finally {
      setLoading(false);
    }
  }, [environmentId, resourceTypeFilter, currentPage, pageSize]);

  useEffect(() => {
    if (environmentId) loadResources();
  }, [environmentId, loadResources]);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Resources
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Discovered AWS resources for this environment
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Filter */}
      <div className="mb-6">
        <Select
          label="Filter by Resource Type"
          value={resourceTypeFilter}
          onChange={(e) => setResourceTypeFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'ec2', label: 'EC2' },
            { value: 's3', label: 'S3' },
            { value: 'rds', label: 'RDS' },
            { value: 'lambda', label: 'Lambda' },
            { value: 'iam', label: 'IAM' },
            { value: 'security_group', label: 'Security Group' },
            { value: 'vpc', label: 'VPC' },
            { value: 'cloudtrail', label: 'CloudTrail' },
          ]}
        />
      </div>

      {/* Resources Table */}
      {resources.length > 0 ? (
        <>
          <Card>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Resource ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ARN</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Region</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Seen</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {resources.map((resource) => (
                      <tr key={resource.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{resource.resource_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{resource.resource_id}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white break-all">{resource.arn || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{resource.region}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{formatDate(resource.last_seen_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            resource.is_deleted
                              ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                              : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                          }`}>
                            {resource.is_deleted ? 'Deleted' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {paginationMeta && ((paginationMeta.totalPages ?? 0) > 1 || (paginationMeta.total ?? 0) > pageSize) && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
                <Select
                  value={String(pageSize)}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  options={[{ value: '10', label: '10' }, { value: '20', label: '20' }, { value: '50', label: '50' }]}
                  placeholder=""
                  className="w-20"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">per page</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>Previous</Button>
                <span className="text-sm text-gray-700 dark:text-gray-300 px-3">
                  Page {currentPage} of {paginationMeta.totalPages || 1}
                  {paginationMeta.total != null && <span className="text-gray-500 dark:text-gray-400 ml-1">({paginationMeta.total} total)</span>}
                </span>
                <Button variant="secondary" size="sm" onClick={() => setCurrentPage((p) => Math.min(paginationMeta.totalPages || 1, p + 1))} disabled={currentPage >= (paginationMeta.totalPages || 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No resources found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No resources match your current filters. Try triggering a scan to discover resources.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ResourcesPage;

